import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { HelpOutline } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  Link,
  MenuItem,
  Skeleton,
  Stack,
  TableContainer,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import GRDialogTitle from '../../../components/GRDialogTitle/GRDialogTitle'
import { GRTable, GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import {
  useCheckGameVersionMutation,
  useCloneAnalysisMutation,
  useCloneAnalysisToMarketMutation,
  useCreateAnalysisForVersionMutation,
  useCreateAnalysisMutation,
  useDeleteAnalysisMutation,
  useGetAnalysisListMarketSettingQuery,
  useGetAnalyzedVersionsPerMarketQuery,
  useGetGameAnalysesQuery,
  useGetGameVersionsQuery,
  useSaveAnalysisListMarketSettingMutation,
} from '../../api/game-analyses'
import { useGetInternalGameDocumentQuery } from '../../api/games'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { useGetInternalStoresQuery } from '../../api/stores'
import { getAnalysisOverviewPath, getCreatedAnalysisPath } from '../../features/analysis/helpers/analysisPaths'
import { AnalysisInfoDialog } from '../../features/games/components/AnalysisInfoDialog/AnalysisInfoDialog'
import { AnalysisInfoSummary } from '../../features/games/components/AnalysisInfoSummary/AnalysisInfoSummary'
import { AnalysisScreenshotsDialog } from '../../features/games/components/AnalysisScreenshotsDialog/AnalysisScreenshotsDialog'
import { AnalyzedVersionsPerMarket } from '../../features/games/components/AnalyzedVersionsPerMarket/AnalyzedVersionsPerMarket'
import {
  formatAnalysisListDate,
  getAllPositions,
  getAnalysisListRows,
  getAnalysisPositions,
  getAnalysisPowerScore,
  getBadgeCloneSourceIso,
  getCloneVersionOptions,
  getDefaultSourcePosition,
  getDefaultTargetPosition,
  getLatestAnalysisId,
  getPositionOptions,
  isSystemAnalysis,
} from '../../features/games/helpers/analysisListHelpers'
import { AnalysisListRow, AnalyzedVersionPerMarket, GameAnalysis, latestGameVersion } from '../../features/games/types/GameAnalyses'
import { CommentStatusIndicator } from '../../features/review/components/CommentStatusIndicator/CommentStatusIndicator'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

const fallbackMarketIso = 'us'

type AnalysisListDialogProps = {
  open: boolean
  gameId?: string
  onClose: () => void
  // Without it the dialog stays open and links to the new analysis.
  onAnalysisCreated?: (analysisId: string, comparedMarketIso?: string) => void
}

type CreatedAnalysis = { id: string; compared?: { gameId: string; marketIso: string } }
type ConfirmAction = { analysis: GameAnalysis } | { perMarket: AnalyzedVersionPerMarket }

type CustomProps = {
  latestId?: string
  isWriting: boolean
  canCreate: boolean
  getPositionName: (analysis: GameAnalysis) => string | undefined
  onNew: (version: string) => void
  onClone: (analysis: GameAnalysis) => void
  onDelete: (analysis: GameAnalysis) => void
  onInfo: (analysis: GameAnalysis) => void
  onScreenshots: (analysis: GameAnalysis) => void
  onOpenAnalysis: () => void
}

export const AnalysisListDialog: FC<AnalysisListDialogProps> = ({ open, gameId, onClose, onAnalysisCreated }) => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [sourceId, setSourceId] = useState<string>()
  const [targetId, setTargetId] = useState<string>()
  const [cloneVersion, setCloneVersion] = useState(latestGameVersion)
  const [created, setCreated] = useState<CreatedAnalysis>()
  const [infoAnalysis, setInfoAnalysis] = useState<GameAnalysis>()
  const [screenshotsAnalysis, setScreenshotsAnalysis] = useState<GameAnalysis>()
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<ConfirmAction>>()
  // Changes whenever the dialog opens or shows another game, so a create started before then isn't applied.
  const sessionRef = useRef(0)

  useEffect(() => {
    sessionRef.current += 1
    setSourceId(undefined)
    setTargetId(undefined)
    setCloneVersion(latestGameVersion)
    setCreated(undefined)
    setInfoAnalysis(undefined)
    setScreenshotsAnalysis(undefined)
  }, [open, gameId])

  const isActive = open && !!gameId
  const gameQuery = useGetInternalGameDocumentQuery(gameId ?? '', { skip: !isActive, refetchOnMountOrArgChange: true })
  const storesQuery = useGetInternalStoresQuery(undefined, { skip: !isActive })
  const marketsQuery = useGetInternalMarketsQuery(undefined, { skip: !isActive })
  const settingQuery = useGetAnalysisListMarketSettingQuery(undefined, { skip: !isActive, refetchOnMountOrArgChange: true })

  const game = gameQuery.currentData
  const appId = game?.appId || undefined
  const gameName = game?.name ?? ''
  const positions = useMemo(
    () => (game && storesQuery.data && marketsQuery.data ? getAnalysisPositions(game, storesQuery.data, marketsQuery.data) : []),
    [game, marketsQuery.data, storesQuery.data]
  )
  const allPositions = useMemo(
    () => (storesQuery.data && marketsQuery.data ? getAllPositions(storesQuery.data, marketsQuery.data) : []),
    [marketsQuery.data, storesQuery.data]
  )
  const arePositionsReady = !!game && !!storesQuery.data && !!marketsQuery.data && (settingQuery.isSuccess || settingQuery.isError)
  const source = positions.find(({ id }) => id === sourceId) ?? getDefaultSourcePosition(positions, settingQuery.data ?? null)
  const target = positions.find(({ id }) => id === targetId) ?? getDefaultTargetPosition(positions)
  const marketIso = source?.marketIso ?? fallbackMarketIso

  const analysesQuery = useGetGameAnalysesQuery({ gameId: gameId ?? '', marketIso }, { skip: !isActive || !arePositionsReady, refetchOnMountOrArgChange: true })
  const versionsQuery = useGetGameVersionsQuery({ appId: appId ?? 0, marketIso }, { skip: !isActive || !arePositionsReady || !appId })
  const usVersionsQuery = useGetGameVersionsQuery({ appId: appId ?? 0, marketIso: fallbackMarketIso }, { skip: !isActive || !appId })
  const perMarketQuery = useGetAnalyzedVersionsPerMarketQuery(gameId ?? '', { skip: !isActive, refetchOnMountOrArgChange: true })

  const [saveSetting] = useSaveAnalysisListMarketSettingMutation()
  const [createAnalysis, createState] = useCreateAnalysisMutation()
  const [cloneAnalysis, cloneState] = useCloneAnalysisMutation()
  const [cloneAnalysisToMarket, cloneToMarketState] = useCloneAnalysisToMarketMutation()
  const [createAnalysisForVersion, createForVersionState] = useCreateAnalysisForVersionMutation()
  const [deleteAnalysis, deleteState] = useDeleteAnalysisMutation()
  const [checkGameVersion, checkVersionState] = useCheckGameVersionMutation()
  const isWriting =
    createState.isLoading ||
    cloneState.isLoading ||
    cloneToMarketState.isLoading ||
    createForVersionState.isLoading ||
    deleteState.isLoading ||
    checkVersionState.isLoading

  const analyses = useMemo(() => analysesQuery.currentData ?? [], [analysesQuery.currentData])
  const versions = useMemo(() => versionsQuery.currentData ?? [], [versionsQuery.currentData])
  const areVersionsReady = !appId || !!versionsQuery.currentData || versionsQuery.isError
  const areRowsReady = !!analysesQuery.currentData && areVersionsReady
  const rows = useMemo(() => (areRowsReady ? getAnalysisListRows(analyses, versions) : []), [analyses, areRowsReady, versions])
  const latestId = useMemo(() => getLatestAnalysisId(analyses), [analyses])
  const cloneVersionOptions = useMemo(() => getCloneVersionOptions(versions), [versions])
  const usVersions = useMemo(() => usVersionsQuery.data ?? [], [usVersionsQuery.data])
  const perMarket = useMemo(() => perMarketQuery.data ?? [], [perMarketQuery.data])
  const badgeSourceIso = getBadgeCloneSourceIso(usVersions, perMarket)

  const runCreate = useCallback(
    async (create: () => Promise<string>, failureKey: string, compared?: CreatedAnalysis['compared']) => {
      const session = sessionRef.current
      setCreated(undefined)
      const createdAnalysis = await run(create(), { error: t(failureKey) })
      if (!createdAnalysis.ok || session !== sessionRef.current) return
      if (onAnalysisCreated) onAnalysisCreated(createdAnalysis.result, compared?.marketIso)
      else setCreated({ id: createdAnalysis.result, compared })
    },
    [onAnalysisCreated, run, t]
  )

  const handleSourceChange = async (id: string) => {
    const position = positions.find((candidate) => candidate.id === id)
    if (!position) return
    setSourceId(id)
    setCloneVersion(latestGameVersion)
    const saved = await run(saveSetting(position.marketIso).unwrap(), { error: t('internal-analysts:games_analyses_market_save_failed') })
    if (saved.ok && saved.result !== position.marketIso) notifyError(t('internal-analysts:games_analyses_market_save_failed'))
  }

  const handleCreate = () =>
    source &&
    gameId &&
    runCreate(() => createAnalysis({ gameId, marketId: source.marketId, storeId: source.storeId }).unwrap(), 'internal-analysts:games_analyses_create_failed')

  const handleCloneToTarget = () =>
    target &&
    gameId &&
    runCreate(
      () => cloneAnalysisToMarket({ gameId, sourceMarketIso: marketIso, targetMarketIso: target.marketIso, gameVersion: cloneVersion }).unwrap(),
      'internal-analysts:games_analyses_clone_failed'
    )

  const handleCheckVersion = async () => {
    if (!gameId) return
    await run(checkGameVersion(gameId).unwrap(), {
      success: t('internal-analysts:games_analyses_version_checked'),
      error: t('internal-analysts:games_analyses_version_check_failed'),
    })
  }

  const handleConfirm = async (confirmed?: ConfirmDialogData<ConfirmAction>) => {
    setConfirmDialogData(undefined)
    const action = confirmed?.data
    if (!action || !gameId) return
    if ('perMarket' in action) {
      if (!badgeSourceIso) return
      const targetMarketIso = action.perMarket.marketIso
      const compared = badgeSourceIso !== targetMarketIso ? { gameId, marketIso: targetMarketIso } : undefined
      await runCreate(
        () => cloneAnalysisToMarket({ gameId, sourceMarketIso: badgeSourceIso, targetMarketIso, gameVersion: latestGameVersion }).unwrap(),
        'internal-analysts:games_analyses_create_failed',
        compared
      )
      return
    }
    setCreated(undefined)
    const deleted = await run(deleteAnalysis({ id: action.analysis.id, gameId, marketIso }).unwrap(), {
      error: t('internal-analysts:games_analyses_delete_failed'),
    })
    if (!deleted.ok) return
    if (deleted.result.some(({ id }) => id === action.analysis.id)) {
      notifyError(t('internal-analysts:games_analyses_not_deleted'))
      return
    }
    notifySuccess(t('internal-analysts:games_analyses_deleted'))
  }

  const customProps = useMemo<CustomProps>(
    () => ({
      latestId,
      isWriting,
      canCreate: !!source && !!gameId,
      getPositionName: (analysis) => allPositions.find(({ storeId, marketId }) => storeId === analysis.storeId && marketId === analysis.marketId)?.fullName,
      onNew: (version) =>
        source &&
        gameId &&
        runCreate(() => createAnalysisForVersion({ version, marketId: source.marketId, gameId }).unwrap(), 'internal-analysts:games_analyses_create_failed'),
      onClone: (analysis) => gameId && runCreate(() => cloneAnalysis({ id: analysis.id, gameId }).unwrap(), 'internal-analysts:games_analyses_create_failed'),
      onDelete: (analysis) =>
        setConfirmDialogData({
          title: t('internal-analysts:games_analyses_delete_title'),
          confirmText: t('internal-analysts:games_analyses_delete_confirm'),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: { analysis },
        }),
      onInfo: setInfoAnalysis,
      onScreenshots: setScreenshotsAnalysis,
      onOpenAnalysis: onClose,
    }),
    [allPositions, cloneAnalysis, createAnalysisForVersion, gameId, isWriting, latestId, onClose, runCreate, source, t]
  )

  const tableColumns: GRTableColumn<AnalysisListRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:games_analyses_version'),
        accessor: ({ row }) => row.gameVersion || '-',
        sortable: true,
        sortAccessor: ({ row }) => row.gameVersion ?? '',
      },
      {
        labelAccessor: t('internal-analysts:games_analyses_date'),
        accessor: ({ row }) => (
          <Box component="span" whiteSpace="nowrap">
            {formatAnalysisListDate(row.ts)}
          </Box>
        ),
        sortable: true,
        sortOrder: SortOrder.DESC,
        sortAccessor: ({ row }) => row.ts,
      },
      {
        labelAccessor: t('internal-analysts:games_analyses_type_and_id'),
        accessor: ({ row: { analysis }, customTableProps }) =>
          analysis ? (
            <Stack alignItems="flex-start">
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{t(isSystemAnalysis(analysis) ? 'internal-analysts:games_analyses_system' : 'internal-analysts:games_analyses_manual')}</span>
                {customTableProps?.latestId === analysis.id && <Chip size="small" color="success" label={t('internal-analysts:games_analyses_latest_stamp')} />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {analysis.id}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="error.main">
              {t('internal-analysts:games_analyses_no_analysis')}
            </Typography>
          ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: t('internal-common:market'),
        accessor: ({ row: { analysis } }) => analysis?.marketIso.toUpperCase() ?? '',
      },
      {
        labelAccessor: t('internal-analysts:games_analyses_gps'),
        accessor: ({ row: { analysis } }) => (analysis ? getAnalysisPowerScore(analysis) ?? '-' : ''),
        sortable: true,
        sortAccessor: ({ row: { analysis } }) => analysis?.powerScore ?? 0,
      },
      {
        labelAccessor: t('internal-analysts:games_analyses_info'),
        accessor: ({ row: { analysis }, customTableProps }) =>
          analysis && (
            <GRTooltip content={<AnalysisInfoSummary analysis={analysis} positionName={customTableProps?.getPositionName(analysis)} />}>
              <Link component="button" variant="body2" onClick={() => customTableProps?.onInfo(analysis)}>
                {t('internal-analysts:games_analyses_show')}
              </Link>
            </GRTooltip>
          ),
      },
      {
        labelAccessor: t('internal-analysts:games_analyses_screens'),
        accessor: ({ row: { analysis }, customTableProps }) => {
          if (!analysis) return ''
          const count = analysis.screenshots?.length ?? 0
          if (!count) return '-'
          return (
            <Link component="button" variant="body2" fontWeight="bold" onClick={() => customTableProps?.onScreenshots(analysis)}>
              {count}
            </Link>
          )
        },
      },
      {
        labelAccessor: t('internal-analysts:games_analyses_comment'),
        accessor: ({ row: { analysis } }) => analysis && <CommentStatusIndicator analysis={analysis} />,
      },
      {
        labelAccessor: '',
        accessor: ({ row: { analysis, gameVersion }, customTableProps }) => {
          const isWriting = !!customTableProps?.isWriting
          const newButton = (version?: string | null) => (
            <Button size="small" disabled={isWriting || !customTableProps?.canCreate || !version} onClick={() => version && customTableProps?.onNew(version)}>
              {t('internal-analysts:games_analyses_new')}
            </Button>
          )
          if (!analysis) return <Box textAlign="right">{newButton(gameVersion)}</Box>
          return (
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              {isSystemAnalysis(analysis) && newButton(analysis.gameVersion)}
              <Button size="small" component={RouterLink} to={getAnalysisOverviewPath(analysis.id)} onClick={customTableProps?.onOpenAnalysis}>
                {t('internal-analysts:games_analyses_edit')}
              </Button>
              <Button size="small" disabled={isWriting} onClick={() => customTableProps?.onClone(analysis)}>
                {t('internal-analysts:games_analyses_clone')}
              </Button>
              <Button size="small" color="error" disabled={isWriting} onClick={() => customTableProps?.onDelete(analysis)}>
                {t('internal-analysts:games_analyses_delete')}
              </Button>
            </Stack>
          )
        },
        cellProps: { align: 'right' },
        headerCellProps: { sx: { width: 260, minWidth: 260 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const sourceOptions = getPositionOptions(positions, source)
  const targetOptions = getPositionOptions(positions, target)

  const renderControls = () => {
    if (!arePositionsReady) return <Skeleton variant="rectangular" height={120} />
    return (
      <Stack spacing={2}>
        {positions.length ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              size="small"
              fullWidth
              label={t('internal-analysts:games_analyses_source')}
              value={source?.id ?? ''}
              disabled={isWriting}
              onChange={(event) => handleSourceChange(event.target.value)}
            >
              {sourceOptions.map((position) => (
                <MenuItem key={position.id} value={position.id}>
                  {position.fullName}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title={t('internal-analysts:games_analyses_positions_help')}>
              <HelpOutline color="action" fontSize="small" />
            </Tooltip>
            <Button variant="contained" disabled={isWriting || !source} onClick={handleCreate} sx={{ flexShrink: 0 }}>
              {t('internal-analysts:games_analyses_create')}
            </Button>
          </Stack>
        ) : (
          <Typography>{t('internal-analysts:games_analyses_no_positions')}</Typography>
        )}
        {analyses.length > 0 && positions.length > 0 && (
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              size="small"
              fullWidth
              label={t('internal-analysts:games_analyses_target')}
              value={target?.id ?? ''}
              disabled={isWriting}
              onChange={(event) => setTargetId(event.target.value)}
            >
              {targetOptions.map((position) => (
                <MenuItem key={position.id} value={position.id}>
                  {position.fullName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('internal-analysts:games_analyses_clone_version')}
              value={cloneVersion}
              disabled={isWriting}
              onChange={(event) => setCloneVersion(event.target.value)}
              sx={{ minWidth: 200 }}
            >
              {cloneVersionOptions.map((version) => (
                <MenuItem key={version} value={version}>
                  {version === latestGameVersion ? t('internal-analysts:games_analyses_latest') : version}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" disabled={isWriting || !target} onClick={handleCloneToTarget} sx={{ flexShrink: 0 }}>
              {t('internal-analysts:games_analyses_clone')}
            </Button>
          </Stack>
        )}
        <Button variant="outlined" disabled={isWriting} onClick={handleCheckVersion}>
          {t('internal-analysts:games_analyses_check_version')}
        </Button>
      </Stack>
    )
  }

  const renderTable = () => {
    if (analysesQuery.isError) return <Typography color="error.main">{t('internal-analysts:games_analyses_load_failed')}</Typography>
    if (!areRowsReady) return <Skeleton variant="rectangular" height={240} />
    if (!rows.length) return <Typography color="text.secondary">{t('internal-analysts:games_analyses_none')}</Typography>
    return (
      <TableContainer>
        <GRTable rows={rows} columns={columns} onColumnsUpdated={setColumns} rowIdKey="id" customProps={customProps} striped hoverable />
      </TableContainer>
    )
  }

  const createdPath = created && gameId && getCreatedAnalysisPath(created.id, gameId, created.compared?.marketIso)
  const handleClose = isWriting ? undefined : onClose

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <GRDialogTitle onClose={handleClose}>{gameQuery.isFetching && !game ? <Skeleton width={240} /> : gameName}</GRDialogTitle>
      <DialogContent>
        {gameQuery.isError ? (
          <Typography color="error.main">{t('internal-analysts:games_load_one_failed')}</Typography>
        ) : (
          <Stack spacing={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AnalyzedVersionsPerMarket
                  newestVersion={usVersions[0]?.version}
                  perMarket={perMarket}
                  isLoading={perMarketQuery.isFetching || usVersionsQuery.isFetching}
                  canClone={!!badgeSourceIso && !isWriting}
                  onClone={(entry) =>
                    setConfirmDialogData({
                      title: t('internal-analysts:games_analyses_clone_latest'),
                      confirmText: t('internal-analysts:games_analyses_clone_latest_confirm', { market: entry.marketName }),
                      actionText: t('internal-analysts:games_analyses_clone'),
                      data: { perMarket: entry },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {renderControls()}
              </Grid>
            </Grid>
            {created && createdPath && (
              <Alert
                severity="success"
                action={
                  <Button color="inherit" size="small" component={RouterLink} to={createdPath} onClick={onClose}>
                    {t('internal-analysts:games_analyses_open')}
                  </Button>
                }
              >
                {t('internal-analysts:games_analyses_created')}
              </Alert>
            )}
            {deleteState.isLoading && <Typography textAlign="center">{t('internal-analysts:games_analyses_deleting')}</Typography>}
            {renderTable()}
          </Stack>
        )}
      </DialogContent>
      <AnalysisInfoDialog
        open={!!infoAnalysis}
        gameName={gameName}
        analysis={infoAnalysis}
        positionName={infoAnalysis && customProps.getPositionName(infoAnalysis)}
        onClose={() => setInfoAnalysis(undefined)}
      />
      <AnalysisScreenshotsDialog
        open={!!screenshotsAnalysis}
        gameName={gameName}
        gameVersion={screenshotsAnalysis?.gameVersion}
        screenshots={screenshotsAnalysis?.screenshots ?? []}
        onClose={() => setScreenshotsAnalysis(undefined)}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Dialog>
  )
}
